<template>
  <span>
    <gl-button @click="$refs.file.click()" :disabled="disabled">
      {{ buttonText }}
    </gl-button>
    <span
      gl-tooltip="商品選択の上限を超えてファイル指定できます"
      gl-tooltip-direction="right"
    >
      <gl-icon icon="announcement" />
    </span>

    <!-- ファイル選択の表示は隠して、他トリガーからファイル選択ダイアログは起動します -->
    <input
      type="file"
      ref="file"
      accept=".txt,.text,.csv,.dat"
      hidden
      @change="search($event.target.files)"
    />
    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      <span>{{ snackbar.message }}</span>
    </md-snackbar>
  </span>
</template>

<script>
// 規定以上のサイズ（少なくとも画面UIに列挙できる商品数（デフォ500））
// 以上の商品を分析するため、ファイルアップロードで一時テーブルを
// 作成する方法に対応したファイル入力コンポーネント
// 最終的にはItemSelectByFileと入れ替えるので名称は適当です。

import { readAsText } from '@/util/localfile'

import ErrorReport from '@/mixins/ErrorReport'

const MAX_ITEMS = process.env.VUE_APP_MAX_ITEMS
  ? Number(process.env.VUE_APP_MAX_ITEMS)
  : 500

// 選択ファイルをローカルで処理（**非**ローカルとは、ファイルを
// アップロードしてサーバーサイドで一時テーブルを作成する方法）
// する最大サイズ（バイト数）
// UI上選択できる最大商品数がおおよそ入力できるくらいのサイズにします。
const LOCAL_HANDLING_MAX_SIZE_BYTES = (MAX_ITEMS - 30) * 13

const STATE_DEFAULT = 'DEFAULT'
const STATE_UPLOADING = 'UPLOADING'

const BUTTON_TEXT = {
  [STATE_DEFAULT]: 'ファイル読込',
  [STATE_UPLOADING]: 'アップロード中…',
}

export default {
  mixins: [ErrorReport],
  props: {
    company: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      state: STATE_DEFAULT,
      /**
       * Snackbarプロパティ
       */
      snackbar: {
        show: false,
        duration: 4000,
        message: '',
      },
    }
  },
  computed: {
    disabled() {
      return this.state !== STATE_DEFAULT
    },
    buttonText() {
      return BUTTON_TEXT[this.state]
    },
    errors() {
      const errors = []
      if (this.disabled) {
        errors.push('ファイルアップロード中です')
      }
      return errors
    },
  },
  methods: {
    async search(files) {
      if (files.length === 0) {
        return
      }

      const file = files[0]

      try {
        this.state = STATE_UPLOADING

        if (file.size <= LOCAL_HANDLING_MAX_SIZE_BYTES) {
          await this.processLocally(file)
        } else {
          await this.processOnServer(file)
        }
      } finally {
        this.state = STATE_DEFAULT
        if (this.$refs.file) {
          this.$refs.file.value = ''
        }
      }
    },
    /**
     * ローカルでファイル内容を読み込んで商品を入力する
     * ※ "ローカル"とは言っていますが、マスタ突合による名称取得などは
     *    サーバーサイドで行います。商品一覧取得後の処理はブラウザ側で
     *    行います。
     */
    async processLocally(file) {
      const content = await readAsText(file)
      const codes = content
        .split('\n')
        .map(v => v.trim())
        .filter(v => v)

      const items = await this.$repositories
        .get('item')
        .getByCode(this.company, codes)

      this.showSnackbar({
        message: codes.length + '件中 ' + items.length + '商品見つかりました',
      })

      this.$emit('selected', items)
    },
    /**
     * 選択ファイルをサーバーにアップロードしてリソースID
     * を取得して処理をする
     */
    async processOnServer(file) {
      const repo = this.$repositories.get('uploadFile')

      const { url, id } = await repo.issueUploadUrl(file.name)
      await repo.upload(url, file)

      let loadResult
      try {
        loadResult = await repo.loadItemFile(id, this.company)
      } catch (error) {
        // ファイルダイアログで拡張子を絞っていますが、
        // ユーザーが手動で「全てのファイル」に変更して選択できる
        // ので、このエラーは発生する可能性があります。
        if (
          error.response.status === 400 &&
          error.response.data.code === 'NOT_SUPPORTED'
        ) {
          const msg =
            `アップロードされたファイルが解析できませんでした。` +
            `サポートされている形式のファイルかご確認ください（${
              error.response.data.supported
            }）。`
          alert(msg)
          return
        }
        throw error
      }

      const { numRows, numOnMaster, numLoadFail } = loadResult

      this.showSnackbar({
        message: `ファイル行数: ${numRows}、マスタ一致件数: ${numOnMaster}、読込エラー件数: ${numLoadFail}`,
      })

      this.$emit('selected', loadResult.itemGroupId)
    },
    /**
     * Snackbarの表示
     */
    showSnackbar(options) {
      this.snackbar.duration = 4000
      this.snackbar.message = options.message
      this.snackbar.show = true
    },
  },
}
</script>
<style lang="scss" scoped>
[gl-tooltip]::after {
  white-space: normal;
  width: max-content;
  max-width: 21em;
}
</style>
